import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { VerifiedPhoneParams } from '../../api/Rest/users';
import {
  LoadingSpinner,
  ResetPasswordSuccessGroup,
} from '../../components/Molecules';
import {
  ExpiredTemplate,
  ResetPasswordTemplate,
} from '../../components/Templates';
import { NOT_APPLICABLE_STRING } from '../../constants/general';
import { AppToastErrorOption } from '../../constants/toast';
import {
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import { useShareData } from '../../context';
import { ResetPasswordResult } from '../../models/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';

const { resetPasswordValidation, resetPassword } = useUserStore.getState();

interface State {
  email: string;
  phone: string;
  password: string;
  retypePassword: string;
  displayEmail: string;
  displayPhone: string;
  initLoading: boolean;
  isLoading: boolean;
  precheckSuccess: boolean;
  errorMessage: string;
  successResetPassword: boolean;
}

interface ResetPasswordScreenProps {
  webPageType: WebPageType;
}

const ResetPasswordScreen: React.FC<ResetPasswordScreenProps> = ({
  webPageType,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const activeUserId = useSessionStore((state) => state.activeUserId);

  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');
  const token = queryParams.get('token');
  const navigationState = location.state as {
    phone: VerifiedPhoneParams;
  } | null;
  const phone = navigationState?.phone;

  const fallbackEmail = new URLSearchParams(
    new URL(window.location.href).search,
  )
    .get('email')
    ?.replace(/ /g, '+');

  const finalEmail = email?.includes(' ') ? fallbackEmail : email;

  const [state, setState] = useState<State>({
    email: finalEmail ?? NOT_APPLICABLE_STRING,
    phone: phone?.phoneNumber ?? NOT_APPLICABLE_STRING,
    password: '',
    retypePassword: '',
    displayEmail: '',
    displayPhone: '',
    initLoading: phone?.phoneNumber ? false : true,
    isLoading: false,
    precheckSuccess: true,
    errorMessage: '',
    successResetPassword: false,
  });

  const onValidateResetPassword = useCallback(async () => {
    if (finalEmail && token) {
      setState((s) => ({ ...s, initLoading: true }));
      const res = await resetPasswordValidation({
        email: finalEmail,
        token: token,
      });
      setState((s) => ({ ...s, initLoading: false }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({ ...s, precheckSuccess: true }));
      } else {
        setState((s) => ({ ...s, precheckSuccess: false }));
      }
    } else {
      setState((s) => ({ ...s, precheckSuccess: false, initLoading: false }));
    }
  }, [finalEmail, token]);

  useEffect(() => {
    if (finalEmail || token) {
      onValidateResetPassword().catch(console.error);
    }
  }, [finalEmail, onValidateResetPassword, token]);

  const onPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, password: event.target.value }));
    },
    [],
  );
  const onRetypePasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, retypePassword: event.target.value }));
    },
    [],
  );

  const onClick = useCallback(async () => {
    if (state.password !== state.retypePassword) {
      setState((s) => ({ ...s, errorMessage: 'Passwords must match' }));
    } else if (state.password.length < 6) {
      setState((s) => ({
        ...s,
        errorMessage: 'Password(s) must have at least 6 characters',
      }));
    } else if (state.password === state.retypePassword && state.password) {
      setState((s) => ({ ...s, errorMessage: '' }));
      if (phone) {
        setState((s) => ({ ...s, isLoading: true }));
        const resData = await resetPassword({
          phone,
          password: state.password,
        });
        setState((s) => ({ ...s, isLoading: false }));
        if (resData) {
          setState((s) => ({
            ...s,
            successResetPassword: true,
            displayEmail: resData.email?.address ?? NOT_APPLICABLE_STRING,
            displayPhone: resData.phone?.phoneNumber ?? NOT_APPLICABLE_STRING,
          }));
        } else {
          toast(t('There is something wrong.'), AppToastErrorOption);
        }
      } else if (finalEmail && token) {
        setState((s) => ({ ...s, isLoading: true }));
        const resData = await resetPassword({
          email: state.email,
          token,
          password: state.password,
        });
        setState((s) => ({ ...s, isLoading: false }));
        if (resData) {
          setState((s) => ({
            ...s,
            successResetPassword: true,
            displayEmail: resData.email?.address ?? NOT_APPLICABLE_STRING,
            displayPhone: resData.phone?.phoneNumber ?? NOT_APPLICABLE_STRING,
          }));
        } else {
          toast(t('There is something wrong.'), AppToastErrorOption);
        }
      }
    }
  }, [
    finalEmail,
    phone,
    state.email,
    state.password,
    state.retypePassword,
    t,
    token,
  ]);

  const onClickLogin = useCallback(() => {
    if (activeUserId) {
      navigate(getWebPageLink(webPageType, WebPageLink.Home));
    } else {
      navigate(getWebPageLink(webPageType, WebPageLink.Entry));
    }
  }, [activeUserId, navigate, webPageType]);

  const onClickHome = useCallback(() => {
    navigate(getWebPageLink(webPageType, WebPageLink.Home));
  }, [navigate, webPageType]);

  return state.initLoading ? (
    <LoadingSpinner loading={state.initLoading} />
  ) : state.precheckSuccess ? (
    state.successResetPassword === false ? (
      <ResetPasswordTemplate
        webPageType={webPageType}
        password={{ value: state.password, onChange: onPasswordChange }}
        retypePassword={{
          value: state.retypePassword,
          onChange: onRetypePasswordChange,
        }}
        onClick={() => {
          onClick().catch(console.error);
        }}
        isLoading={state.isLoading}
        errorMessage={state.errorMessage}
      />
    ) : (
      <ResetPasswordSuccessGroup
        email={state.displayEmail}
        phone={state.displayPhone}
        webPageType={webPageType}
        onClickLogin={onClickLogin}
      />
    )
  ) : (
    <ExpiredTemplate
      description="The link has expired."
      onClick={onClickHome}
    />
  );
};

export default ResetPasswordScreen;
